import api from '../services/api';
import { useState, useEffect } from 'react';

let feeSettingsCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Get fee showing settings from payment settings
 * @returns {Promise<boolean>} Whether fees should be shown
 */
export const getFeeShowingSettings = async () => {
  try {
    // Check cache first
    if (feeSettingsCache && cacheTimestamp && (Date.now() - cacheTimestamp) < CACHE_DURATION) {
      return feeSettingsCache;
    }

    const response = await api.get('/payment-settings');
    if (response.data.success) {
      const showFees = response.data.data.general?.showFees ?? true;
      
      // Update cache
      feeSettingsCache = showFees;
      cacheTimestamp = Date.now();
      
      return showFees;
    }
    
    return true; // Default to showing fees if API fails
  } catch (error) {
    console.warn('Failed to fetch fee showing settings:', error);
    return true; // Default to showing fees on error
  }
};

/**
 * Clear the fee settings cache (useful after settings are updated)
 */
export const clearFeeSettingsCache = () => {
  feeSettingsCache = null;
  cacheTimestamp = null;
};

/**
 * Hook to use fee showing settings in components
 * @returns {Object} { showFees, loading, error }
 */
export const useFeeSettings = () => {
  const [showFees, setShowFees] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const shouldShowFees = await getFeeShowingSettings();
        setShowFees(shouldShowFees);
        setError(null);
      } catch (err) {
        setError(err);
        setShowFees(true); // Default to showing fees
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return { showFees, loading, error };
}; 